import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRecorderContext } from '@/lib/recorder/recorder-context';
import { Icon } from '@/components/atoms/icon';
import {
  usePracticeQueue,
  useCreatePracticeSession,
  useAddArrangementToSession,
  useGetPracticeSessionById,
  usePracticeSessionQueue,
  snapshotItemToQueueItem,
  type PracticeSessionQueueItem,
} from '@/lib/queries/practice-queue';
import { useAssessmentLevels } from '@/lib/queries/reference-data';
import { useUpdatePracticeSession } from '@/lib/queries/use-update-practice-session';
import { InstrumentSelector } from '@/components/instrument-selector';
import { Button } from '@/components/atoms/button';
import { EmptyState } from '@/components/molecules/empty-state';
import { MasteryBadge } from '@/components/molecules/mastery-badge';
import { PracticeStage, type PracticeStageProps } from '@/components/organisms/practice-stage';
import { useViewport } from '@/lib/hooks/use-viewport';
import { useTabletOrientation } from '@/lib/hooks/use-tablet-orientation';
import type { PracticeStageVariant } from '@/components/organisms/practice-stage';
import { cn } from '@/lib/cn';
import type { QueueItem } from '@/lib/types';

// Default-valued params are omitted from the URL: validateSearch returns
// undefined for an absent/empty session so it does not serialize into the
// query string. The committed practice-session id is the only URL state here;
// recording phase and the pre-session instrument pick stay local (#426 / #203).
type PracticeSearch = {
  session?: string;
};

export const Route = createFileRoute('/practice')({
  component: PracticePage,
  validateSearch: (raw: Record<string, unknown>): PracticeSearch => ({
    session: typeof raw.session === 'string' && raw.session !== '' ? raw.session : undefined,
  }),
});

type AssessedItem = {
  item: QueueItem;
  assessmentLevelId: string;
  assessmentName: string;
};

type IdleState = { phase: 'idle' };
type ActiveState = {
  phase: 'active';
  sessionId: string;
  startTime: Date;
  currentIndex: number;
  assessed: AssessedItem[];
  isRunning: boolean;
  queueSnapshot: PracticeSessionQueueItem[];
};
type CompleteState = {
  phase: 'complete';
  sessionId: string;
  assessed: AssessedItem[];
  durationSeconds: number;
};
type SessionState = IdleState | ActiveState | CompleteState;


function PiecePreviewRow({
  item,
  index,
  variant,
}: {
  item: QueueItem;
  index: number;
  variant: 'focus' | 'maintenance';
}): ReactElement {
  const metadata = [item.keyName, item.formName].filter(Boolean).join(' · ') || item.arrangementName;

  return (
    <li
      className={cn(
        'flex items-center gap-4 rounded-[var(--r-md)] border border-[var(--rule)] p-4',
        variant === 'focus' ? 'bg-[var(--bg-surface)]' : 'bg-[var(--bg-soft)]',
      )}
    >
      <div
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--r-sm)] font-mono text-sm font-bold',
          'bg-[var(--bg-soft)] text-[var(--ink-3)]',
        )}
        aria-hidden
      >
        {String(index + 1).padStart(2, '0')}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-base font-semibold text-[var(--ink)]">
          {item.pieceName}
        </p>
        <p className="mt-0.5 truncate text-xs text-[var(--ink-3)]">{metadata}</p>
      </div>
      <MasteryBadge sortOrder={item.masteryLevelSortOrder} />
    </li>
  );
}

function IdlePhase({
  instrumentId,
  onInstrumentChange,
  onStartPractice,
  isStarting,
  focusPieces,
  maintenancePieces,
  isQueueLoading,
}: {
  instrumentId: string | null;
  onInstrumentChange: (id: string) => void;
  onStartPractice: () => void;
  isStarting: boolean;
  focusPieces: QueueItem[];
  maintenancePieces: QueueItem[];
  isQueueLoading: boolean;
}): ReactElement {
  const totalCount = focusPieces.length + maintenancePieces.length;

  return (
    <div className="mx-auto max-w-3xl space-y-8 px-4 py-6 pb-24 lg:px-8 lg:py-10">
      <header className="space-y-2">
        <h1 className="font-display text-3xl font-semibold tracking-tight text-[var(--ink)]">
          Practice
        </h1>
        <p className="text-sm text-[var(--ink-3)]">
          Here&apos;s today&apos;s queue. Pick your instrument and start when ready.
        </p>
      </header>

      <section className="space-y-4 rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] p-6">
        <InstrumentSelector value={instrumentId} onChange={onInstrumentChange} />
        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={onStartPractice}
          disabled={!instrumentId || isStarting || focusPieces.length === 0}
        >
          {isStarting ? 'Starting…' : 'Start Practice'}
        </Button>
      </section>

      <section aria-labelledby="focus-queue-heading" className="space-y-4">
        <div className="flex items-end justify-between">
          <div className="space-y-1">
            <h2
              id="focus-queue-heading"
              className="font-display text-xl font-semibold text-[var(--ink)]"
            >
              Focus Queue
            </h2>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)]">
              {focusPieces.length} focus · {maintenancePieces.length} maintenance
            </p>
          </div>
        </div>

        {isQueueLoading ? (
          <div className="space-y-3" aria-busy>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-16 animate-pulse rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-soft)]"
              />
            ))}
          </div>
        ) : totalCount === 0 ? (
          <EmptyState
            icon="music"
            title="Nothing in the queue yet"
            description="Add pieces to your repertoire and they'll show up here ready to practice."
            action={
              <Link to="/repertoire">
                <Button size="lg" fullWidth>
                  <Icon name="plus" size={16} /> Go to repertoire
                </Button>
              </Link>
            }
          />
        ) : (
          <>
            {focusPieces.length > 0 && (
              <ul className="space-y-3">
                {focusPieces.map((item, index) => (
                  <PiecePreviewRow
                    key={item.userArrangementId}
                    item={item}
                    index={index}
                    variant="focus"
                  />
                ))}
              </ul>
            )}

            {maintenancePieces.length > 0 && (
              <div className="space-y-3 pt-4">
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)]">
                  Maintenance Queue
                </h3>
                <ul className="space-y-3">
                  {maintenancePieces.map((item, index) => (
                    <PiecePreviewRow
                      key={item.userArrangementId}
                      item={item}
                      index={focusPieces.length + index}
                      variant="maintenance"
                    />
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

type ActivePhaseProps = Omit<PracticeStageProps, 'variant'>;

// Tablet keys on width only (useViewport), but portrait tablets have no room
// for the landscape drone side panel — so portrait gets its own stage variant
// driven by useTabletOrientation. Every other viewport passes straight through.
function effectiveVariant(
  viewport: ReturnType<typeof useViewport>,
  orientation: ReturnType<typeof useTabletOrientation>,
): PracticeStageVariant {
  if (viewport === 'tablet' && orientation === 'portrait') return 'tablet-portrait';
  return viewport;
}

function ActivePhase(props: ActivePhaseProps): ReactElement {
  const viewport = useViewport();
  const orientation = useTabletOrientation();
  return (
    <PracticeStage
      variant={effectiveVariant(viewport, orientation)}
      startTime={props.startTime}
      isRunning={props.isRunning}
      queue={props.queue}
      currentIndex={props.currentIndex}
      assessmentLevels={props.assessmentLevels}
      onToggleTimer={props.onToggleTimer}
      onFinish={props.onFinish}
      onAssess={props.onAssess}
      onSkip={props.onSkip}
      onEndPractice={props.onEndPractice}
      isAssessing={props.isAssessing}
    />
  );
}


function PracticePage(): ReactElement | null {
  const { session: sessionIdParam } = Route.useSearch();
  const [sessionState, setSessionState] = useState<SessionState>({ phase: 'idle' });
  const [instrumentId, setInstrumentId] = useState<string | null>(null);
  const navigate = useNavigate({ from: '/practice' });

  const { data: queueData, isLoading: isQueueLoading } = usePracticeQueue();
  const { data: assessmentLevelsData } = useAssessmentLevels();
  const createSession = useCreatePracticeSession();
  const addArrangement = useAddArrangementToSession();
  const updateSession = useUpdatePracticeSession();
  const queryClient = useQueryClient();
  const { setActivePracticeSessionId } = useRecorderContext();

  // Rehydrate a committed session from the URL after a reload. While the page
  // already drives the session locally (start → active → summary), the
  // ?session= param is the source of truth that survives a refresh.
  const needsRehydration = sessionState.phase === 'idle' && sessionIdParam !== undefined;
  const { data: rehydratedSession } = useGetPracticeSessionById(
    needsRehydration ? sessionIdParam : null,
  );
  const { data: rehydratedQueue } = usePracticeSessionQueue(
    needsRehydration ? sessionIdParam : null,
  );

  useEffect(() => () => setActivePracticeSessionId(null), [setActivePracticeSessionId]);

  // When the committed session leaves the URL (e.g. the back button) — a
  // present->absent transition of the param — drop the active session and
  // return to the idle start screen. Tracking the previous value (rather than
  // a plain mismatch) avoids resetting during start, when local state leads the
  // URL by a render before navigate() commits the new ?session=.
  const previousSessionIdParam = useRef(sessionIdParam);
  useEffect(() => {
    const left = previousSessionIdParam.current !== undefined && sessionIdParam === undefined;
    previousSessionIdParam.current = sessionIdParam;
    if (left && sessionState.phase === 'active') {
      setActivePracticeSessionId(null);
      setSessionState({ phase: 'idle' });
    }
  }, [sessionIdParam, sessionState.phase, setActivePracticeSessionId]);

  useEffect(() => {
    if (!needsRehydration || rehydratedSession === undefined) {
      return;
    }
    // An ended session (duration set) has nothing to resume — show its summary.
    if (rehydratedSession.durationMinutes !== null) {
      void navigate({
        to: '/practice/sessions/$sessionId/summary',
        params: { sessionId: rehydratedSession.practiceSessionId },
      });
      return;
    }
    if (rehydratedQueue === undefined) {
      return;
    }
    setActivePracticeSessionId(rehydratedSession.practiceSessionId);
    setSessionState({
      phase: 'active',
      sessionId: rehydratedSession.practiceSessionId,
      startTime: new Date(rehydratedSession.practicedAt),
      currentIndex: 0,
      assessed: [],
      isRunning: true,
      queueSnapshot: rehydratedQueue,
    });
  }, [
    needsRehydration,
    rehydratedSession,
    rehydratedQueue,
    setActivePracticeSessionId,
    navigate,
  ]);

  const focusPieces = queueData?.focusPieces ?? [];
  const maintenancePieces = queueData?.maintenancePieces ?? [];
  const assessmentLevels = assessmentLevelsData?.content ?? [];

  const handleStartPractice = async (): Promise<void> => {
    if (!instrumentId) return;
    const session = await createSession.mutateAsync({
      instrumentId,
      practicedAt: new Date().toISOString(),
    });
    setActivePracticeSessionId(session.practiceSessionId);
    void navigate({ search: (prev) => ({ ...prev, session: session.practiceSessionId }) });
    setSessionState({
      phase: 'active',
      sessionId: session.practiceSessionId,
      startTime: new Date(session.practicedAt),
      currentIndex: 0,
      assessed: [],
      isRunning: true,
      queueSnapshot: session.queue ?? [],
    });
  };

  const handleToggleTimer = (): void => {
    if (sessionState.phase !== 'active') return;
    setSessionState({ ...sessionState, isRunning: !sessionState.isRunning });
  };

  const completeSession = (state: ActiveState, assessedOverride?: AssessedItem[]): void => {
    const durationMinutes = Math.max(1, Math.ceil((Date.now() - state.startTime.getTime()) / 60_000));
    void queryClient.invalidateQueries({ queryKey: ['practice-queue'] });
    setActivePracticeSessionId(null);
    setSessionState({
      phase: 'complete',
      sessionId: state.sessionId,
      assessed: assessedOverride ?? state.assessed,
      durationSeconds: durationMinutes * 60,
    });
    void updateSession.mutateAsync({ sessionId: state.sessionId, durationMinutes }).then(() => {
      void navigate({
        to: '/practice/sessions/$sessionId/summary',
        params: { sessionId: state.sessionId },
      });
    });
  };

  const handleAssess = async (assessmentLevelId: string): Promise<void> => {
    if (sessionState.phase !== 'active') return;
    const activeQueue: QueueItem[] = sessionState.queueSnapshot.map(snapshotItemToQueueItem);
    const currentItem = activeQueue[sessionState.currentIndex];
    if (!currentItem) return;

    await addArrangement.mutateAsync({
      sessionId: sessionState.sessionId,
      arrangementId: currentItem.arrangementId,
      assessmentLevelId,
    });

    const assessmentLevel = assessmentLevels.find((l) => l.assessmentLevelId === assessmentLevelId);
    const assessedItem: AssessedItem = {
      item: currentItem,
      assessmentLevelId,
      assessmentName: assessmentLevel?.prettyName ?? '',
    };
    const nextAssessed = [...sessionState.assessed, assessedItem];

    const nextIndex = sessionState.currentIndex + 1;
    if (nextIndex >= activeQueue.length) {
      completeSession(sessionState, nextAssessed);
    } else {
      setSessionState({
        ...sessionState,
        currentIndex: nextIndex,
        assessed: nextAssessed,
      });
    }
  };

  const handleSkip = (): void => {
    if (sessionState.phase !== 'active') return;
    const nextIndex = sessionState.currentIndex + 1;
    if (nextIndex >= sessionState.queueSnapshot.length) {
      completeSession(sessionState);
    } else {
      setSessionState({ ...sessionState, currentIndex: nextIndex });
    }
  };

  const handleEndPractice = (): void => {
    if (sessionState.phase !== 'active') return;
    completeSession(sessionState);
  };

  const handleFinishFromTimer = (): void => {
    if (sessionState.phase !== 'active') return;
    completeSession(sessionState);
  };

  if (sessionState.phase === 'idle') {
    return (
      <IdlePhase
        instrumentId={instrumentId}
        onInstrumentChange={setInstrumentId}
        onStartPractice={() => void handleStartPractice()}
        isStarting={createSession.isPending}
        focusPieces={focusPieces}
        maintenancePieces={maintenancePieces}
        isQueueLoading={isQueueLoading}
      />
    );
  }

  if (sessionState.phase === 'active') {
    const activeQueue: QueueItem[] = sessionState.queueSnapshot.map(snapshotItemToQueueItem);
    return (
      <ActivePhase
        startTime={sessionState.startTime}
        isRunning={sessionState.isRunning}
        currentIndex={sessionState.currentIndex}
        queue={activeQueue}
        assessmentLevels={assessmentLevels}
        onToggleTimer={handleToggleTimer}
        onFinish={handleFinishFromTimer}
        onAssess={(id) => void handleAssess(id)}
        onSkip={handleSkip}
        onEndPractice={handleEndPractice}
        isAssessing={addArrangement.isPending}
      />
    );
  }

  // completeSession() navigates to the summary route immediately, so reaching
  // this point with phase === 'complete' is transient. Render nothing while the
  // router handles the navigation.
  return null;
}
